import { useEffect } from 'react';
import { useNavigateBack } from 'hooks';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { ApplicationFormPageEnum } from '../types';
import { IRefinancingForm } from './types';
import { Button, FormCurrencyField, FormSelect } from 'components/common';
import { refinancingQuestions } from './questions';
import { ISelectOption } from 'components/common/Select/types';
import { getCurrentRoute } from 'utils/location';
import { applicationFormApi } from 'api/applicationForm';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { parseApplicationInProgress } from '../helpers';
import { extractFinancingAmountUpperLimitByFtv } from '../../CalculatorPage/FinanceDetailsPage/helpers';
import { countFtvValue, updateError } from './helpers';
import {
  MAX_DEPOSIT_TO_PROPERTY_PRICE_RATIO,
  MIN_DEPOSIT_TO_PROPERTY_PRICE_RATIO,
} from '../../CalculatorPage/criteria';

export const Refinancing = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const intl = useIntl();
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const validate = (value: string | number | null, max: number): string | true => {
    if (value !== null && value !== '') {
      if (Number(value) > max) return intl.formatMessage({ id: 'applicationForm.REFINANCING.financeAmount.maxError' });
    }

    return true;
  };

  const validateMinDepositToProperty = (propertyPrice: number, deposit: number) => {
    if (propertyPrice > 0 && deposit) {
      if (deposit < propertyPrice * MIN_DEPOSIT_TO_PROPERTY_PRICE_RATIO) {
        return intl.formatMessage(
          { id: 'applicationForm.REFINANCING.deposit.maxError' },
          { maxDepositPercentage: `${MAX_DEPOSIT_TO_PROPERTY_PRICE_RATIO * 100}%` },
        );
      }
    }
  };

  const form = useForm<IRefinancingForm>({
    defaultValues: {
      propertyWorth: '',
      refinanceAmount: '',
      outstandingCurrentMortgage: '',
      extraFinancePlanning: null,
    },
  });

  const refinanceAmount = form.watch('refinanceAmount');
  const propertyWorth = form.watch('propertyWorth');

  useEffect(() => {
    const deposit = (Number(propertyWorth) || 0) - (Number(refinanceAmount) || 0);
    const ftv = countFtvValue(deposit, Number(refinanceAmount));

    updateError(form, 'refinanceAmount', validate(refinanceAmount, extractFinancingAmountUpperLimitByFtv(ftv)));
    updateError(form, 'refinanceAmount', validateMinDepositToProperty(Number(propertyWorth), deposit));
  }, [refinanceAmount, propertyWorth]);

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const formState = form.watch();
  const isAnyFieldWithError = Boolean(Object.values(form.formState.errors).length);

  const submitFormHandler = async (formData: IRefinancingForm) => {
    const questions = Object.entries(formData).map(([key, answer]) => ({
      question: intl.formatMessage({ id: refinancingQuestions[key].labelKey }),
      key,
      answer: answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId });

    searchParams.set('section', ApplicationFormPageEnum.REGION);
    setSearchParams(searchParams);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div className='mt-5'>
          <FormattedMessage id={refinancingQuestions.propertyWorth.labelKey} tagName={'h4'} />
          <FormCurrencyField
            name={'propertyWorth'}
            rules={{
              required: requiredFieldErrorMessage,
              validate: (value: string) => {
                if (value && Number(value) < 75000) {
                  return intl.formatMessage({ id: 'common.error.minNumber' }, { min: '£75,000' });
                }
                return true;
              },
            }}
          />
        </div>

        <div className='mt-5'>
          <FormattedMessage id={refinancingQuestions.refinanceAmount.labelKey} tagName={'h4'} />
          <FormattedMessage id={refinancingQuestions.refinanceAmount.explanationKey} tagName={'p'} />
          <FormCurrencyField name={'refinanceAmount'} rules={{ required: requiredFieldErrorMessage, min: 1 }} />
        </div>

        <div className='mt-5'>
          <FormattedMessage id={refinancingQuestions.outstandingCurrentMortgage.labelKey} tagName={'h4'} />
          <FormattedMessage id={refinancingQuestions.outstandingCurrentMortgage.explanationKey} tagName={'p'} />
          <FormCurrencyField
            name={'outstandingCurrentMortgage'}
            rules={{ required: requiredFieldErrorMessage, min: 1 }}
          />
        </div>

        <div className='mt-5'>
          <FormattedMessage id={refinancingQuestions.extraFinancePlanning.labelKey} tagName={'h4'} />
          <FormattedMessage id={refinancingQuestions.extraFinancePlanning.explanationKey} tagName={'p'} />
          <FormSelect
            name={'extraFinancePlanning'}
            options={refinancingQuestions.extraFinancePlanning.options}
            className={'w-full'}
            selectProps={{
              isOptionEqualToValue: (option: ISelectOption, selectedOptionValue: string) =>
                option.value === selectedOptionValue,
              value: formState.extraFinancePlanning,
            }}
            selectInputProps={{ placeholder: intl.formatMessage({ id: 'placeholders.select' }) }}
            rules={{ required: requiredFieldErrorMessage }}
          />
          {formState.extraFinancePlanning === 'applicationForm.REFINANCING.options.other' && (
            <p className={'text-red-500'}>
              <FormattedMessage id={'applicationForm.REFINANCING.other.warning'} />
            </p>
          )}
        </div>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'} disabled={isAnyFieldWithError}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
